import React, { useState, useEffect } from "react";
import { Menu, Drawer, Grid, Button } from "antd";
import {
  MenuOutlined,
  FileTextOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import * as Icons from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { BASE_URL } from "../Components/Constant";
import ApiService from "../Components/apiService";
import eventBus from "../Components/eventBus";
import { useSelector } from "react-redux";

const { useBreakpoint } = Grid;

const AppSidebar = ({ onSidebarToggle, collapsedWidth, expandedWidth }) => {
  // Persist collapsed state in localStorage
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true"; // default: expanded (false)
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [visible, setVisible] = useState(false);

  const security = useSelector((state) => state.data.items[0]);
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedSelectedKey = localStorage.getItem("selectedKey");
    setSelectedKey(savedSelectedKey);

    // Function to fetch tabs for a given appId
    const fetchTabs = async (appId) => {
      try {
        setLoading(true);
        const apiService = new ApiService(
          `${BASE_URL}/mt_tabs/${appId}`,
          {},
          "GET",
        );
        const response = await apiService.makeCall();

        const filteredItems = response
          .filter((item) => item.icon !== null && item.active === true)
          .map((item) => {
            console.log("tab item", item);
            const IconComponent = Icons[item.icon];
            return {
              key:
              item.tab_category === "custom_component"
                ? String(item._id)
                : String(item.mt_object_id), // ✅ FORCE STRING
              label: item.label,
              icon: IconComponent ? (
                <IconComponent style={{ fontSize: "20px" }} />
              ) : null,
              objectName: item.object_name || item.label.toLowerCase(),
              order: item.order,
              component_name: item.component_name,
              tab_category: item.tab_category,
            };
          })
          .sort((a, b) => a.order - b.order);

        setItems(filteredItems);
      } catch (error) {
        console.error("Error fetching tabs:", error);
      } finally {
        setLoading(false);
      }
    };

    // Listen for app change events
    const appChangedListener = (data) => {
      if (data?.appId) {
        console.log("App changed event received:", data);
        fetchTabs(data.appId);
      }
    };

    eventBus.on("AppChanged", appChangedListener);

    return () => {
      eventBus.off("AppChanged", appChangedListener);
    };
  }, []); // no dependency on apps, only respond to events

  useEffect(() => {
    const matchedItem = items.find((item) => {
      const isObjectPath = location.pathname.startsWith(`/object/${item.key}`);
      const isRecordPath = location.pathname.startsWith(`/record/${item.key}`);
      return isObjectPath || isRecordPath;
    });
    setSelectedKey(matchedItem ? matchedItem.key : null);
  }, [location.pathname, items]);

  // Sync sidebar width with parent on mount and when collapsed changes
  useEffect(() => {
    console.log("Sidebar collapsed state changed:", collapsed);
    if (onSidebarToggle) {
      onSidebarToggle(collapsed ? collapsedWidth : expandedWidth);
    }
  }, [collapsed]);

  useEffect(() => {
    const toggleHandler = () => {
      setCollapsed((prev) => {
        const newState = !prev;
        localStorage.setItem("sidebarCollapsed", newState); // ✅ persist
        return newState;
      });
    };

    eventBus.on("toggleSidebar", toggleHandler);

    return () => {
      eventBus.off("toggleSidebar", toggleHandler);
    };
  }, []);

  const handleClick = (e) => {
    console.log("Menu item clicked:", e);
    const clickedItem = items.find((item) => item.key === String(e.key));
    console.log("clicked item", clickedItem);

    // Reports
    if (e.key === "reports") {
      navigate("/setup/Reports");
    }

    if (!clickedItem) return;

    setSelectedKey(e.key);
    localStorage.setItem("selectedKey", e.key);

    // ✅ NEW: custom component navigation
    if (clickedItem.tab_category === "custom_component") {
      console.log("Navigating to custom component");
      navigate(`/customcomponent/${clickedItem.component_name}`);
      return;
    }

    // ✅ Existing object navigation
    if (clickedItem.objectName) {
      navigate(`/object/${e.key}`);
      return;
    }

    
  };

  // Only collapse/expand with arrow button, persist state
  const toggleCollapsed = () => {
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    localStorage.setItem("sidebarCollapsed", newCollapsed);
    if (onSidebarToggle) {
      onSidebarToggle(newCollapsed ? collapsedWidth : expandedWidth);
    }
  };

  const showDrawer = () => {
    setVisible(true);
  };

  const onClose = () => {
    setVisible(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("selectedKey");
  };

  return (
    <div style={{ display: "flex" }}>
      {screens.md ? (
        <div
          style={{
            width: collapsed ? collapsedWidth : expandedWidth,
            transition: "width 0.2s",
            height: "calc(100vh - 64px)",
            marginTop: "57px",
            position: "fixed",
            zIndex: 1001,
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            fontSize: "12px",
            padding: "0",
          }}
        >
          {loading ? (
            <p style={{ color: "#999", fontSize: "12px", margin: "8px 0" }}></p>
          ) : (
            <div
              style={{
                height: "100%",
                overflowY: "auto",
              }}
              className="custom-scrollbar"
            >
              <Menu
                onClick={handleClick}
                selectedKeys={[selectedKey]}
                mode="vertical"
                inlineCollapsed={collapsed}
                style={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "#ffffff",
                  fontSize: "12px",
                  padding: "0",
                }}
                items={[
                  ...items.map((item) => ({
                    ...item,
                    icon: item.icon
                      ? React.cloneElement(item.icon, {
                          style: { fontSize: "16px", marginRight: "4px" },
                        })
                      : null,
                  })),
                  ...(security?.system_settings?.report.view
                    ? [
                        {
                          key: "reports",
                          label: "Reports",
                          icon: (
                            <FileTextOutlined
                              style={{
                                fontSize: "16px",
                                color: "#1677ff",
                                marginRight: "4px",
                              }}
                            />
                          ),
                        },
                      ]
                    : []),
                ]}
                theme="light"
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <Button
            icon={<MenuOutlined style={{ fontSize: "16px" }} />}
            onClick={showDrawer}
            style={{
              position: "fixed",
              top: "68px",
              left: "8px",
              zIndex: 1000,
              padding: "4px",
              fontSize: "12px",
            }}
          />
          <Drawer
            title="Menu"
            placement="left"
            onClose={onClose}
            visible={visible}
            bodyStyle={{ backgroundColor: "#ffffff", padding: "0" }}
          >
            <Menu
              onClick={handleClick}
              selectedKeys={[selectedKey]}
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "transparent",
                fontSize: "12px",
                padding: "0",
              }}
              mode="vertical"
              items={[
                ...items.map((item) => ({
                  ...item,
                  icon: item.icon
                    ? React.cloneElement(item.icon, {
                        style: { fontSize: "16px", marginRight: "4px" },
                      })
                    : null,
                })),
                ...(security?.system_settings?.report.view
                  ? [
                      {
                        key: "reports",
                        label: "Reports",
                        icon: (
                          <FileTextOutlined
                            style={{
                              fontSize: "16px",
                              color: "#1677ff",
                              marginRight: "4px",
                            }}
                          />
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </Drawer>
        </>
      )}
      <div style={{ flexGrow: 1 }}></div>
    </div>
  );
};

export default AppSidebar;
